import { AnalyzeAPI } from "@ai360/core";
import { injectIntl, useIntl } from "@ai360/core/intl";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { preventBubbleUp, toCamelCase } from "~/admin/utils";
import { DataTable, DialogBox, DialogBoxFooterType, Loader, LoaderTypes } from "~/core";
import ApiResultsWrapper from "~/core/api/components/api-results-wrapper";
import { IDataTableFooterOptions, IIndex } from "~/core/components/tables/interfaces";
import { ISearchData, ISortOptions } from "~/core/components/tables/interfaces";
import { setBreadcrumbs } from "~/sliding-panel/data/actions";
import { useAppDispatch } from "~/store/hooks";
import QueryExtractionAddEditPanel from "./add-edit/add-edit-panel";
import { model, service } from "./data";
import { IQueryExtractionRow } from "./data/sagas";
import { messages } from "./i18n-messages";

import "./query-extraction.css";
type QueryExtractionFilters = {
    description?: string;
    lastModified?: string;
    name?: string;
    queryId?: string;
};

const DEFAULT_PAGE_SIZE = 50;

export const QueryExtraction_ = () => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const [filter, setFilter] = useState<QueryExtractionFilters>({});
    const [showAddEditPanel, setShowAddEditPanel] = useState(false);
    const [autoSearchData, setAutoSearchData] = useState<ISearchData>();
    const [selectedRow, setSelectedRow] = useState<null | IQueryExtractionRow>(null);
    const [sortOptions, setSortOptions] = useState<ISortOptions>({});
    const [pageOptions, setPageOptions] = useState({ pageSize: DEFAULT_PAGE_SIZE, skip: 0 });

    const [isLoading, setIsLoading] = useState(false);
    const [records, setRecords] = useState<IQueryExtractionRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [queryIdsToDelete, setQueryIdsToDelete] = useState<number[]>([]);

    const selectedItemsForTable = useMemo(
        () => selectedItems.map((queryId) => ({ queryId })),
        [selectedItems]
    );

    const toQueryIds = (items: { queryId: number }[] = []): number[] =>
        items.map((item) => item.queryId);

    const onDeleteSelectedAction = (deleteData: IIndex) => {
        const reported = toQueryIds(deleteData?.selectedItems as unknown as { queryId: number }[]);
        const ids = reported.length > 0 ? reported : selectedItems;
        if (ids.length === 0) {
            return;
        }
        setQueryIdsToDelete(ids);
    };

    const onSelectAll = async () => {
        setIsLoading(true);
        try {
            const request = buildListRequest();
            const response = await AnalyzeAPI.getSelectAll(request);
            setSelectedItems(response);
        } catch (err) {
            setRecords([]);
            setTotalCount(0);

            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const onDeleteRowAction = (deleteData: IIndex) => {
        const ids = toQueryIds(deleteData?.selectedItems as unknown as { queryId: number }[]);
        if (ids.length === 0) {
            return;
        }
        setQueryIdsToDelete(deleteData?.deleteFromRow ? ids.slice(0, 1) : ids);
    };

    const tableFooterOptions: IDataTableFooterOptions[] = [
        {
            action: onDeleteSelectedAction,
            disableIfNotSelected: true,
            label: formatMessage(messages.deleteSelectedButton),
        },
    ];

    const sortOptionsRef = useRef<ISortOptions>(sortOptions);

    useEffect(() => {
        sortOptionsRef.current = sortOptions;
    }, [sortOptions]);

    useEffect(() => {
        dispatch(setBreadcrumbs());
    }, [showAddEditPanel]);

    const toPascalCase = (attr: string) =>
        attr ? `${attr[0].toUpperCase()}${attr.slice(1)}` : undefined;

    const buildListRequest = useCallback((): AnalyzeAPI.QueryListRequest => {
        const pageNumber = Math.floor(pageOptions.skip / pageOptions.pageSize) + 1;
        return {
            description: filter.description || undefined,
            modifiedDate: filter.lastModified || undefined,
            name: filter.name || undefined,
            pageNumber,
            pageSize: pageOptions.pageSize,
            queryId: filter.queryId ? Number(filter.queryId) : undefined,
            sortBy: toPascalCase(toCamelCase(sortOptions?.FieldName || model.PROPS_QUERY_ID)),
            sortDesc: sortOptions?.Sort?.Direction === "DESC" ? true : undefined,
        };
    }, [filter, pageOptions, sortOptions]);

    const fetchList = useCallback(async () => {
        setIsLoading(true);
        try {
            const request = buildListRequest();
            const response = await AnalyzeAPI.getQueryList(request);
            const rows: IQueryExtractionRow[] = response.data.map((item) => ({
                canDelete: true,
                dayOfMonth: null,
                dayOfWeek: null,
                description: item.description,
                fileName: "",
                frequency: "",
                lastModified: item.modifiedDate,
                name: item.name,
                outputUrl: "",
                queryId: item.queryId,
                queryText: "",
            }));
            setRecords(rows);
            setTotalCount(response.metaResponseData.totalRows);
        } catch (err) {
            setRecords([]);
            setTotalCount(0);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [buildListRequest]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const onShowAddEditPanel = (_, action: "ADD" | "EDIT", editData: { queryId: number }) => {
        let record: null | IQueryExtractionRow = null;
        if (action === "EDIT") {
            record = records.find((r) => r.queryId === editData.queryId) ?? null;
        }
        setSelectedRow(record);
        setShowAddEditPanel(true);
    };

    const getAutoSearchList = async (attr: string, value: string) => {
        const attrName = toCamelCase(attr);

        const searchNameMap: Record<string, string> = {
            [model.PROPS_DESCRIPTION]: "Description",
            [model.PROPS_LAST_MODIFIED]: "ModifiedDate",
            [model.PROPS_NAME]: "Name",
            [model.PROPS_QUERY_ID]: "QueryId",
        };

        const searchName = searchNameMap[attrName];
        if (!searchName || !value) {
            setAutoSearchData({ response: [], searchName: attrName });
            return;
        }

        try {
            const results = await AnalyzeAPI.getQueryAutoComplete({
                description: filter.description || undefined,
                modifiedDate: filter.lastModified || undefined,
                name: filter.name || undefined,
                queryId: filter.queryId ? Number(filter.queryId) : undefined,
                searchName,
                searchValue: value,
            });

            const searchData: ISearchData = {
                response: results.map((r) => ({ value: r.value })),
                searchName: attrName,
            };
            setAutoSearchData(searchData);
        } catch (err) {
            setAutoSearchData({ response: [], searchName: attrName });
            throw err;
        }
    };

    const onFilterChange = (attr: string, value: string, _: any) => {
        const attrName = toCamelCase(attr);
        const filterQuery = { ...filter, [attrName]: value ? value : undefined };
        setFilter(filterQuery);
        setPageOptions((prev) => ({ ...prev, skip: 0 }));
    };

    const onConfirmDelete = async () => {
        if (queryIdsToDelete.length === 0) {
            return;
        }
        setIsLoading(true);
        try {
            await Promise.all(queryIdsToDelete.map((queryId) => AnalyzeAPI.deleteQuery(queryId)));
        } finally {
            setQueryIdsToDelete([]);
            setSelectedItems([]);
            fetchList();
        }
    };

    return (
        <ApiResultsWrapper clearErrorOnUnmount={false}>
            <div className="query-extraction">
                <div className="content-table-container query-table">
                    {!isLoading ? null : <Loader type={LoaderTypes.LINE_SCALE} />}
                    <DataTable
                        autoSearchList={autoSearchData}
                        formatMessage={formatMessage as any}
                        isEditable
                        forcedRender
                        deleteSelected={onDeleteRowAction}
                        footerOptions={tableFooterOptions}
                        getAutoSearchList={getAutoSearchList}
                        isCheckbox={true}
                        messages={messages as any}
                        onFilterChange={onFilterChange}
                        onPaginatorChange={(index) => {
                            if (index.pageSize) {
                                setPageOptions({
                                    pageSize: Number(index.pageSize),
                                    skip: index.skip || 0,
                                });
                            }
                        }}
                        onSelect={(items) => {
                            const next = toQueryIds(items as unknown as { queryId: number }[]);
                            setSelectedItems((prev) =>
                                prev.length === next.length && prev.every((id, i) => id === next[i])
                                    ? prev
                                    : next
                            );
                        }}
                        onSelectAll={onSelectAll}
                        onSortOption={({ sortOptions: nextSortOptions }) => {
                            setSortOptions(nextSortOptions || service.defaultSort);
                        }}
                        openAddEditPanel={onShowAddEditPanel}
                        records={records}
                        selectedItems={selectedItemsForTable as any}
                        service={service}
                        setLastEditedRow={() => {}}
                        totalCount={totalCount}
                    />
                    {!showAddEditPanel ? null : (
                        <form onSubmit={(event) => preventBubbleUp(event)}>
                            <QueryExtractionAddEditPanel
                                closePanel={() => {
                                    setSelectedRow(null);
                                    setShowAddEditPanel(false);
                                    fetchList();
                                }}
                                selectedRow={selectedRow as any}
                            />
                        </form>
                    )}
                </div>
            </div>

            <DialogBox
                action={"Delete"}
                className="delete-confirmation-modal"
                draggable={true}
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                isOpen={queryIdsToDelete.length > 0}
                onAction={onConfirmDelete}
                onClose={() => {
                    setQueryIdsToDelete([]);
                }}
                testId="query-extraction-confirm-delete-modal"
                title={formatMessage(messages.deleteConfirmationTitle)}
            >
                <p>
                    {queryIdsToDelete.length == 1
                        ? formatMessage(messages.deleteConfirmationMessage)
                        : formatMessage(messages.deleteSelectedConfirmationMessage, {
                              count: queryIdsToDelete.length,
                          })}
                </p>
            </DialogBox>
        </ApiResultsWrapper>
    );
};

export const QueryExtraction = injectIntl(QueryExtraction_);
